/*
 * SK Orbit — the six colourways on a 3D ring that turns with the scroll,
 * surrounded by falling sakura petals. Three.js is only downloaded when the
 * section gets close to the viewport.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var threePromise = null;

  function loadThree(src) {
    if (window.THREE) return Promise.resolve(window.THREE);
    if (threePromise) return threePromise;
    threePromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { resolve(window.THREE); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return threePromise;
  }

  function webglAvailable() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) {
      return false;
    }
  }

  function setup(section) {
    if (section.hasAttribute('data-sk-orbit-init')) return;
    section.setAttribute('data-sk-orbit-init', '');

    var items = Array.from(section.querySelectorAll('[data-sk-orbit-item]')).map(function (li) {
      return { src: li.getAttribute('data-src'), name: li.getAttribute('data-name'), url: li.getAttribute('data-url') };
    });
    if (!items.length || !window.gsap || !window.ScrollTrigger) return;

    var canvas = section.querySelector('[data-sk-orbit-canvas]');
    var stage = section.querySelector('.sk-orbit__stage');
    var nameEl = section.querySelector('[data-sk-orbit-name]');
    var indexEl = section.querySelector('[data-sk-orbit-index]');
    var totalEl = section.querySelector('[data-sk-orbit-total]');
    var linkEl = section.querySelector('[data-sk-orbit-link]');
    var N = items.length;
    var step = (Math.PI * 2) / N;
    var progress = 0;

    totalEl.textContent = String(N).padStart(2, '0');
    // The section is tall and its stage is position: sticky (CSS), so no GSAP pin
    // is needed: we only read the scroll progress through the section.
    // No refresh here: this runs before window load, and ScrollTrigger re-measures everything then.
    section.classList.add('is-sticky');

    var trigger = ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: function (self) { progress = self.progress; },
    });
    var isSmall = window.matchMedia('(max-width: 749px)').matches;

    var visible = false;
    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
    }, { rootMargin: '200px' }).observe(section);

    var startWhenNear = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      startWhenNear.disconnect();
      // WebGL is probed only now: a throwaway context at page load cost phones a frame.
      (webglAvailable() ? loadThree(section.getAttribute('data-three-src')) : Promise.reject(new Error('no webgl')))
        .then(function (THREE) { build(THREE); })
        .catch(function () {
          trigger.kill();
          section.classList.remove('is-sticky');
          ScrollTrigger.refresh();
        });
    }, { rootMargin: '150% 0px' });
    startWhenNear.observe(section);

    function build(THREE) {
      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isSmall ? 1.5 : 2));
      if ('outputColorSpace' in renderer && THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
      else renderer.outputEncoding = THREE.sRGBEncoding;

      var scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x151311, 9, 17);
      var camera = new THREE.PerspectiveCamera(32, 1, 0.1, 60);

      var R = 3.3;
      var H = 3.1;
      var W = H * 0.75;
      var arc = W / R;

      var ring = new THREE.Group();
      ring.rotation.x = 0.06;
      scene.add(ring);

      var loader = new THREE.TextureLoader();
      var cards = items.map(function (item, i) {
        var start = i * step - arc / 2;
        var geo = new THREE.CylinderGeometry(R, R, H, 48, 1, true, start, arc);
        var front = new THREE.MeshBasicMaterial({ color: 0x333333, side: THREE.FrontSide, transparent: true, opacity: 0 });
        var back = new THREE.MeshBasicMaterial({ color: 0x24201d, side: THREE.BackSide });
        var mesh = new THREE.Mesh(geo, front);
        var inner = new THREE.Mesh(geo, back);
        ring.add(mesh);
        ring.add(inner);

        loader.load(item.src, function (tex) {
          if ('colorSpace' in tex && THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
          else tex.encoding = THREE.sRGBEncoding;
          tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
          var img = tex.image;
          var imgAspect = img.width / img.height;
          var cardAspect = W / H;
          if (imgAspect > cardAspect) {
            tex.repeat.set(cardAspect / imgAspect, 1);
            tex.offset.set((1 - tex.repeat.x) / 2, 0);
          } else {
            tex.repeat.set(1, imgAspect / cardAspect);
            tex.offset.set(0, (1 - tex.repeat.y) / 2);
          }
          front.map = tex;
          front.needsUpdate = true;
          gsap.to(front, { opacity: 1, duration: 1.2, ease: 'power2.out', delay: i * 0.08 });
        });
        return { front: front, angle: i * step };
      });

      // Falling petals
      var petalShape = new THREE.Shape();
      petalShape.moveTo(0, 0);
      petalShape.bezierCurveTo(0.09, 0.05, 0.11, 0.17, 0.0, 0.26);
      petalShape.bezierCurveTo(-0.11, 0.17, -0.09, 0.05, 0, 0);
      var petalGeo = new THREE.ShapeGeometry(petalShape, 6);
      petalGeo.translate(0, -0.13, 0);
      var petalMat = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide, transparent: true, opacity: 0.92 });
      var COUNT = isSmall ? 55 : 130;
      var petals = new THREE.InstancedMesh(petalGeo, petalMat, COUNT);
      var dummy = new THREE.Object3D();
      var seeds = [];
      var tint = new THREE.Color();
      for (var k = 0; k < COUNT; k++) {
        seeds.push({
          x: (Math.random() - 0.5) * 12,
          y: (Math.random() - 0.5) * 8,
          z: (Math.random() - 0.5) * 9,
          speed: 0.25 + Math.random() * 0.45,
          sway: 0.4 + Math.random() * 0.9,
          phase: Math.random() * Math.PI * 2,
          spin: 0.4 + Math.random() * 1.4,
          scale: 0.55 + Math.random() * 0.8,
        });
        tint.setHSL(0.97, 0.45 + Math.random() * 0.2, 0.74 + Math.random() * 0.14);
        petals.setColorAt(k, tint);
      }
      scene.add(petals);

      var pointer = { x: 0, y: 0 };
      var cam = { x: 0, y: 0.35 };
      section.addEventListener('pointermove', function (e) {
        var r = section.getBoundingClientRect();
        pointer.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
        pointer.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      }, { passive: true });

      function resize() {
        var w = stage.clientWidth;
        var h = stage.clientHeight;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.position.z = R + (camera.aspect < 1 ? 9.5 / Math.max(camera.aspect, 0.5) * 0.62 : 8.2);
        camera.updateProjectionMatrix();
      }
      resize();
      // Follow the stage itself (rotation, address bar, pane resize…), not only window resizes.
      if (window.ResizeObserver) new ResizeObserver(resize).observe(stage);
      else window.addEventListener('resize', resize);

      var rotation = 0;
      var active = -1;
      var clock = new THREE.Clock();

      function setActive(i) {
        if (i === active) return;
        active = i;
        var item = items[i];
        indexEl.textContent = String(i + 1).padStart(2, '0');
        linkEl.setAttribute('href', item.url);
        gsap.fromTo(nameEl, { yPercent: 60, opacity: 0 }, {
          yPercent: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'expo.out',
          onStart: function () { nameEl.textContent = item.name; },
        });
      }

      function tick() {
        if (!visible) return;
        var dt = Math.min(clock.getDelta(), 0.05);
        var t = clock.elapsedTime;

        var target = -progress * step * (N - 1) + Math.sin(t * 0.35) * 0.04;
        rotation += (target - rotation) * (isSmall ? 0.16 : 0.09);
        ring.rotation.y = rotation;

        cards.forEach(function (c) {
          var facing = Math.cos(c.angle + rotation);
          var light = 0.42 + 0.58 * Math.max(0, facing);
          c.front.color.setScalar(light);
        });

        var idx = Math.round(-rotation / step) % N;
        if (idx < 0) idx += N;
        setActive(idx);

        cam.x += (pointer.x * 0.7 - cam.x) * 0.05;
        cam.y += (0.35 - pointer.y * 0.35 - cam.y) * 0.05;
        camera.position.x = cam.x;
        camera.position.y = cam.y;
        camera.lookAt(0, 0, 0);

        for (var k = 0; k < COUNT; k++) {
          var s = seeds[k];
          s.y -= s.speed * dt;
          if (s.y < -4.2) s.y = 4.2;
          dummy.position.set(
            s.x + Math.sin(t * s.sway + s.phase) * 0.45,
            s.y,
            s.z + Math.cos(t * s.sway * 0.7 + s.phase) * 0.3
          );
          dummy.rotation.set(t * s.spin + s.phase, t * s.spin * 0.6, Math.sin(t + s.phase) * 0.8);
          dummy.scale.setScalar(s.scale);
          dummy.updateMatrix();
          petals.setMatrixAt(k, dummy.matrix);
        }
        petals.instanceMatrix.needsUpdate = true;

        renderer.render(scene, camera);
      }

      gsap.ticker.add(tick);
      section.classList.add('is-3d');
      ScrollTrigger.refresh();
    }
  }

  function init(scope) {
    scope.querySelectorAll('[data-sk-orbit]').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
